import { debounce } from 'lodash';
import { endOfDay, startOfDay } from 'date-fns';
import React from 'react';
import { userSearchDebounce } from '../components/hooks/use-search';
import { useWrapRequest } from 'use-wrap-request';
import { useSearchReducer } from '../components/hooks/useSearchReducer';
import { Api } from '../core/api';
import { ScheduledPostStatus } from '@api/ChallengeClient';
import { injectTSDI } from '../core/tsdi';

export type ScheduledPostStatusFilter = ScheduledPostStatus | 'ALL';

export function useFeedPlanningStore() {
    const { challengeClient } = injectTSDI(Api);

    const [status, setStatus] =
        React.useState<ScheduledPostStatusFilter>('ALL');
    const [communityId, setCommunityId] = React.useState<number | undefined>(
        undefined
    );
    const [from, setFrom] = React.useState<Date | null>(null);
    const [to, setTo] = React.useState<Date | null>(null);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    const posts = useWrapRequest(
        async (
            searchParams?: QueryParamsDto,
            status?: ScheduledPostStatusFilter,
            communityId?: number,
            from?: Date | null,
            to?: Date | null
        ) => {
            const result =
                await challengeClient.support.scheduledPostSupportControllerGetPage(
                    {
                        page: searchParams?.page || 0,
                        size: searchParams?.size || 30,
                        search: searchParams?.search,
                        sort: searchParams?.sort,
                        status: status && status !== 'ALL' ? status : undefined,
                        communityId,
                        publishFrom: from
                            ? startOfDay(from).toISOString()
                            : undefined,
                        publishTo: to ? endOfDay(to).toISOString() : undefined
                    }
                );

            return {
                ...result.data,
                content: result.data.content || []
            };
        },
        {
            defaultData: {
                content: [],
                totalElements: 0,
                number: 0,
                size: 30,
                totalPages: 0
            }
        }
    );

    const refresh = React.useCallback(() => {
        posts.request([searchParams, status, communityId, from, to]);
    }, [posts, searchParams, status, communityId, from, to]);

    const debouncedAPICall = React.useCallback(
        debounce(
            (
                searchParams?: QueryParamsDto,
                status?: ScheduledPostStatusFilter,
                communityId?: number,
                from?: Date | null,
                to?: Date | null
            ) => {
                posts.request([searchParams, status, communityId, from, to]);
            },
            100
        ),
        [posts]
    );

    React.useEffect(() => {
        debouncedAPICall(searchParams, status, communityId, from, to);
    }, [searchParams, status, communityId, from, to, debouncedAPICall]);

    const clearFilters = () => {
        setStatus('ALL');
        setCommunityId(undefined);
        setFrom(null);
        setTo(null);
        dispatch({ type: 'page', page: 0 });
    };

    return {
        posts,
        searchParams,
        status,
        setStatus,
        communityId,
        setCommunityId,
        from,
        setFrom,
        to,
        setTo,
        clearFilters,
        setSearchTerm,
        dispatch,
        refresh
    };
}
